import { useEffect, useMemo, useState } from 'react';
import { ASSET_OFFICE_PLACEHOLDER_SRC } from '../constants/assetOfficePlaceholder';
import { wirePathToImageDisplaySrc } from '../utils/assetImageResolve';

type AssetImageThumbProps = {
  /** Resolved image URL, or null when the asset has no image. */
  src: string | null;
  className?: string;
  placeholderClassName?: string;
  imgLoading?: 'lazy' | 'eager';
};

/**
 * Thumbnail that falls back to the generic office placeholder when `src` is missing or fails to load.
 */
export function AssetImageThumb({
  src,
  className,
  placeholderClassName,
  imgLoading = 'lazy',
}: AssetImageThumbProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const usePlaceholder = !src || failed;
  const displaySrc = usePlaceholder ? ASSET_OFFICE_PLACEHOLDER_SRC : src;

  return (
    <img
      src={displaySrc}
      alt=""
      className={[className, usePlaceholder ? placeholderClassName : ''].filter(Boolean).join(' ')}
      loading={imgLoading}
      decoding="async"
      onError={() => {
        if (!usePlaceholder) {
          setFailed(true);
        }
      }}
    />
  );
}

type PickedFileImageThumbProps = {
  /** URL/path stored in `images[]`, or a bare filename after a browser file pick. */
  wirePath: string | null;
  /** When set, preview uses an object URL from the picked file (browser file inputs). */
  previewFile?: File;
  /** Data URL (or other ready `img` src) from the parent — preferred over {@link previewFile}. */
  previewSrc?: string | null;
  className?: string;
  wrapClassName?: string;
};

/** Create/update form thumbnail: data-URL / object-URL preview for picked files; HTTPS/path URLs otherwise. */
export function PickedFileImageThumb({
  wirePath,
  previewFile,
  previewSrc,
  className,
  wrapClassName,
}: PickedFileImageThumbProps) {
  const objectUrl = useMemo(
    () => (previewFile ? URL.createObjectURL(previewFile) : null),
    [previewFile],
  );

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const pickedPreviewSrc =
    (typeof previewSrc === 'string' && previewSrc.trim() ? previewSrc : null) ??
    (previewFile && objectUrl ? objectUrl : null);
  const src = pickedPreviewSrc ?? wirePathToImageDisplaySrc(wirePath);

  const img = (
    <AssetImageThumb
      src={src}
      className={className}
      imgLoading={pickedPreviewSrc ? 'eager' : 'lazy'}
    />
  );

  if (wrapClassName) {
    return <div className={wrapClassName}>{img}</div>;
  }
  return img;
}
